/**
 * tutorTodoAdapter — les tâches du masteur (`state.data.tutors.tasks`) → `UITutorTodo[]`, la liste
 * « À faire · n » de « Ma journée ». Et la règle du rappel mensuel « Confirme tes créneaux ».
 *
 * API-aware (`GET /api/v1/tutor/tasks`, `TutorController::getTasks` : `prepare_live`,
 * `missing_reports`, `complete_profile`, `update_availability`, `stripe_account` de `TutorTrait`,
 * `training_videos` de `TutorTraining::homeTask`). Jamais importé par le DS.
 *
 * Ports littéraux de symboles qui vivent dans `src/tutor/**` — interdit à `ports/**`
 * (`src/ports/CLAUDE.md` § Dependency rules) — recopiés, avec leur fichier :
 *
 * - l'ordre et les gestes de `taskList` — `tutor/home/components/TutorTaskBanner.js`
 * - la carte « Formation » et son « Reprendre » — `tutor/home/TrainingCompletionCard.js`
 * - `availabilityNudgeFromState` et `AVAILABILITY_FRESHNESS` — `tutor/availability/maDispoSlot.js`
 * - le mois du rappel et sa clé de stockage — `tutor/availability/MaDispoMonthlyReminder.js`
 *
 * Tant que l'ancien accueil vit derrière `tutor_dsFF`, ces symboles restent une seconde copie qui
 * peut diverger : une évolution d'une règle se reporte ici.
 */
import moment from "moment"

import { championSlugsToNames } from "../../../../../data/constants/championsConstants"
import { countLabel } from "../../../ui/formatters/textFormatter"
import { type UITutorTodo } from "../../../ui/types/tutorCoquilleAccueil"

// ── Formes du serveur ───────────────────────────────────────────────────────

/** `TutorTrait::getPrepareLiveTask` — `count` compte des ÉLÈVES à préparer, pas des lives. */
export type ApiPrepareLiveTask = {
    required?: boolean | null
    count?: number | null
    has_checkpoint_feature?: boolean | null
}

/** `TutorTrait::getMissingReportsTask` — `last_occurrence_id` : la plus récente des séances sans compte-rendu. */
export type ApiMissingReportsTask = {
    required?: boolean | null
    count?: number | null
    last_occurrence_id?: number | string | null
}

/** `TutorTrait::getCompleteProfileTask`. */
export type ApiCompleteProfileTask = {
    required?: boolean | null
    completion_percentage?: number | null
    missing_specialty_subjects?: boolean | null
}

/** `TutorTrait::getUpdateAvailabilityTask` — `freshness` : `verified` / `to_confirm` / `unverified`. */
export type ApiUpdateAvailabilityTask = {
    required?: boolean | null
    freshness?: string | null
}

/** `TutorTrait::getStripeAccountTask`. */
export type ApiStripeAccountTask = {
    required?: boolean | null
    details_submitted?: boolean | null
}

/** `TutorTraining::homeTask` — `status` : `not_started` / `failed` / `passed`, `null` hors formation. */
export type ApiTrainingTask = {
    required?: boolean | null
    status?: string | null
}

/**
 * `state.data.tutors.tasks` — le réducteur `data/tutors.js` range chaque clé du payload, `null`
 * quand elle manque, et ajoute `lastFetchTime`. Avant le premier `getTutorTasks`, chaque clé vaut
 * `null` : la liste est vide, comme l'ancienne bannière qui ne montait pas.
 */
export type ApiTutorTasks = {
    prepare_live?: ApiPrepareLiveTask | null
    missing_reports?: ApiMissingReportsTask | null
    complete_profile?: ApiCompleteProfileTask | null
    update_availability?: ApiUpdateAvailabilityTask | null
    stripe_account?: ApiStripeAccountTask | null
    training_videos?: ApiTrainingTask | null
    lastFetchTime?: number | null
}

/**
 * Qui voit quoi sur l'ancien accueil (`TutorHomeTabScreen.js`) : la bannière des tâches sous le
 * droit `read my_tutor_profile_completion` (et un `user.id`), la carte « Formation » sous le rôle
 * `tutor`. Le view-model les lit sur le compte et les passe ; absents, les deux valent vrai.
 */
export type TutorTodoOptions = {
    showTasks?: boolean
    showTraining?: boolean
}

// ── « À faire » ─────────────────────────────────────────────────────────────

/** Un compteur exploitable : un entier positif, sinon rien (le libellé retombe sur sa forme sans nombre). */
const positiveCount = (value: unknown): number | undefined =>
    typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined

/**
 * Les tâches du masteur, dans l'ordre de priorité de `TutorTaskBanner` : préparer le live (P0),
 * les comptes-rendus manquants (P1), confirmer ses créneaux (P2), compléter son profil (P3),
 * enregistrer son compte Stripe (P4) — puis la formation, en dernier comme la carte qui se posait
 * sous la bannière (arbitrage 8 de `parite-tuteur/coquille-accueil.md`).
 *
 * Une tâche n'apparaît que si le serveur la dit `required` — jamais recalculée ici : le serveur est
 * le seul propriétaire de chaque règle (le doublon profil / créneaux y est déjà tranché).
 *
 * Les mots sont ceux des maquettes (`tutorCoquilleAccueilMocks.ts`), qui reprennent ceux de
 * l'ancienne bannière ; le nombre, que l'ancienne carte portait en pastille, passe dans le texte :
 * `<TodoRow>` n'a pas de pastille.
 */
export function toUITutorTodos(tasks: ApiTutorTasks | null | undefined, options: TutorTodoOptions = {}): UITutorTodo[] {
    const { showTasks = true, showTraining = true } = options
    if (!tasks) return []

    const list: UITutorTodo[] = []

    if (showTasks) {
        const prepare = tasks.prepare_live
        if (prepare?.required) {
            const count = positiveCount(prepare.count)
            list.push({
                id: "prepare_live",
                kind: "prepare_live",
                label: "Prépare ton live",
                detail:
                    (count != null ? `${countLabel(count, "élève")} à préparer. ` : "") +
                    // Le champion qui démarre la séance d'un stage : nommé par le catalogue des champions.
                    `Choisis les matières et les chapitres pour permettre à ${championSlugsToNames.sam} de démarrer la session avec chaque élève.`,
                cta: "Préparer",
                count,
                route: "tutor/prepare-live",
            })
        }

        const missing = tasks.missing_reports
        if (missing?.required) {
            const count = positiveCount(missing.count)
            list.push({
                id: "missing_reports",
                kind: "missing_reports",
                eyebrow: "Urgent",
                label:
                    count != null
                        ? countLabel(count, "compte-rendu manquant", "comptes-rendus manquants")
                        : "Comptes-rendus de lives manquants",
                detail: `Merci de ${count === 1 ? "le" : "les"} compléter rapidement.`,
                cta: "Compléter",
                count,
                route: "end-session-tutor",
                // La clé que l'ancienne bannière poussait (`occurrence_id`), nourrie de
                // `last_occurrence_id` : le bilan s'ouvre sur la séance manquante la plus récente.
                routeParams: { occurrence_id: missing.last_occurrence_id ?? undefined },
            })
        }

        if (tasks.update_availability?.required) {
            list.push({
                id: "update_availability",
                kind: "update_availability",
                // Le verbe et l'objet de « Ma dispo » elle-même : un seul geste, partout les mêmes mots.
                label: "Confirme tes créneaux",
                detail: "Une fois par semaine, pour continuer à recevoir des propositions de groupes.",
                cta: "Confirmer",
                route: "tutor/ma-dispo",
            })
        }

        if (tasks.complete_profile?.required) {
            list.push({
                id: "complete_profile",
                kind: "complete_profile",
                label: "Complète ton profil de tuteur",
                detail: "Indique tes informations de profil pour commencer à prendre des groupes de lives.",
                cta: "Compléter",
                route: "tutor/complete-profile",
            })
        }

        if (tasks.stripe_account?.required) {
            // Pas de route : le geste est une requête (`tutorTodoAction`), l'URL Stripe est signée
            // pour ce compte et n'existe qu'une fois demandée.
            list.push({
                id: "stripe_account",
                kind: "stripe_account",
                label: "Enregistre ton compte Stripe",
                detail: "Cela te permettra de recevoir tes paiements.",
                cta: "Je m'enregistre",
            })
        }
    }

    const training = tasks.training_videos
    if (showTraining && training?.required) {
        // Le quiz raté une fois : la carte disait « Reprendre », avec son texte d'encouragement.
        const resuming = training.status === "failed"
        list.push({
            id: "training",
            kind: "training",
            label: "Formation",
            detail: resuming
                ? "Tu y es presque ! Revois les points qui t'ont manqué et repasse le quiz quand tu te sens prêt·e."
                : "Avant ton premier cours, regarde la vidéo de formation puis valide un petit quiz de 20 questions.",
            cta: resuming ? "Reprendre" : "Commencer",
            route: "tutor/formation",
        })
    }

    return list
}

/**
 * Ce que l'écran fait d'une ligne touchée :
 *
 * - `navigate` — `navigation.push(route, params)`, comme l'ancienne bannière ;
 * - `stripe_form_url` — `apiRequest(skillSetsService.getStripeFormUrl, {}, {}, { email })` avec
 *   l'e-mail du compte, puis l'ouverture de `data.url` (l'ancienne bannière : même onglet sur le
 *   web, `window.open(url, "_self")`, navigateur intégré ailleurs). La ligne reste inerte le temps
 *   de la requête (arbitrage 7).
 */
export type TutorTodoAction =
    { type: "navigate"; route: string; params?: Record<string, unknown> } | { type: "stripe_form_url" }

export function tutorTodoAction(todo: Pick<UITutorTodo, "kind" | "route" | "routeParams">): TutorTodoAction | null {
    if (todo.kind === "stripe_account") return { type: "stripe_form_url" }
    if (!todo.route) return null
    return todo.routeParams != null
        ? { type: "navigate", route: todo.route, params: todo.routeParams }
        : { type: "navigate", route: todo.route }
}

// ── Le rappel mensuel « Confirme tes créneaux » ─────────────────────────────

/** `AVAILABILITY_FRESHNESS.VERIFIED` (`maDispoSlot.js`, miroir de `TutorAvailability::STATE_VERIFIED`). */
const AVAILABILITY_VERIFIED = "verified"

/**
 * La clé AsyncStorage du rappel — la MÊME que l'ancien `MaDispoMonthlyReminder` : un masteur qui
 * passe d'un accueil à l'autre ne revoit pas le rappel déjà vu ce mois-ci.
 */
export const TUTOR_AVAILABILITY_REMINDER_STORAGE_KEY = "@ma_dispo_monthly_reminder_month"

/**
 * Le masteur est-il relancé sur ses créneaux ? Port de `availabilityNudgeFromState` : QUI est
 * relancé est le verdict du serveur (`update_availability.required`), jamais recalculé ; la
 * fraîcheur du compte (`currentUser.object.profile.availability_freshness`) est relue par-dessus,
 * parce que confirmer sa semaine met le compte à jour tout de suite, alors que les tâches attendent
 * le prochain `getTutorTasks`.
 *
 * Faux tant que les tâches ne sont pas arrivées : un rappel mensuel montré trop tôt brûle le mois.
 */
export function tutorAvailabilityNudge(
    tasks: ApiTutorTasks | null | undefined,
    availabilityFreshness: string | null | undefined
): boolean {
    return tasks?.update_availability?.required === true && availabilityFreshness !== AVAILABILITY_VERIFIED
}

/** Le mois que la clé retient, en heure locale comme l'ancien rappel : « 2026-11 ». */
export function tutorAvailabilityReminderMonth(now: Date = new Date()): string {
    return moment(now).format("YYYY-MM")
}

/**
 * Faut-il ouvrir le rappel ? Une fois par mois civil, au premier passage du mois, et seulement
 * pour un masteur relancé (`tutorAvailabilityNudge`). `storedMonth` est ce que la clé contient
 * (`null` jamais vu).
 *
 * Le crochet n'écrit `tutorAvailabilityReminderMonth(now)` sous la clé qu'une fois la boîte
 * montrée — comme l'ancien : brûler le mois avant d'afficher taisait le rappel pour un masteur qui
 * ne l'avait jamais vu.
 */
export function tutorAvailabilityReminderDue(
    nudged: boolean,
    storedMonth: string | null | undefined,
    now: Date = new Date()
): boolean {
    return nudged && storedMonth !== tutorAvailabilityReminderMonth(now)
}
